import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Download, ChevronRight, Search, Paperclip } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";

const STATUS_BADGE = {
  received:   { label: "Received",   cls: "bg-stone-200 text-stone-700" },
  costing:    { label: "Costing",    cls: "bg-sky-100 text-sky-800" },
  quote_sent: { label: "Quote Sent", cls: "bg-amber-100 text-amber-800" },
  won:        { label: "Won",        cls: "bg-emerald-100 text-emerald-800" },
  lost:       { label: "Lost",       cls: "bg-red-100 text-red-800" },
  dropped:    { label: "Dropped",    cls: "bg-stone-300 text-stone-800" },
};

function shortValue(v) {
  if (!v || Number(v) === 0) return "—";
  const n = Number(v);
  if (n >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(2)} L`;
  return formatINR(n, { decimals: 0 });
}

export default function RfqList() {
  const { user } = useAuth();
  // R42 — read URL status param so dashboard win-rate sub-counts can deep-link.
  const initialUrlParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const [rfqs, setRfqs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: initialUrlParams.get("q") || "",
    status: initialUrlParams.get("status") || "",
    customer_id: initialUrlParams.get("customer_id") || "",
    unit_id: initialUrlParams.get("unit_id") || "",
  });
  const [expanded, setExpanded] = useState(new Set());
  // Date-wise sort of the RFQ list (by Received date). Client-side; "" = default order.
  const [dateSort, setDateSort] = useState("desc");   // "desc" = newest first, "asc" = oldest first

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.unit_id && user?.role === "admin") params.unit_id = filters.unit_id;
      const [r, c, u] = await Promise.all([
        api.get("/rfqs", { params }),
        api.get("/customers"),
        user?.role === "admin" ? api.get("/units") : Promise.resolve({ data: [] }),
      ]);
      setRfqs(r.data);
      setCustomers(c.data);
      setUnits(u.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const kpi = useMemo(() => {
    const total = rfqs.length;
    const won = rfqs.filter(r => r.status === "won").length;
    const lost = rfqs.filter(r => r.status === "lost").length;
    const dropped = rfqs.filter(r => r.status === "dropped").length;
    const open = total - won - lost - dropped;
    return { total, open, won, lost, dropped };
  }, [rfqs]);

  // Date-wise sort by Received date (blank dates sink to the bottom either way).
  const sortedRfqs = useMemo(() => {
    if (!dateSort) return rfqs;
    const arr = [...rfqs];
    arr.sort((a, b) => {
      const da = String(a.received_date || ""), db2 = String(b.received_date || "");
      if (!da && !db2) return 0;
      if (!da) return 1;           // undated rows last
      if (!db2) return -1;
      return dateSort === "asc" ? da.localeCompare(db2) : db2.localeCompare(da);
    });
    return arr;
  }, [rfqs, dateSort]);

  function toggle(rfqId) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(rfqId)) next.delete(rfqId); else next.add(rfqId);
      return next;
    });
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="rfq-list-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            RFQ Register
            <span className="text-base font-medium text-slate-400">·</span>
            <span className="text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5" data-testid="rfq-total-received">
              Total received: {kpi.total}
            </span>
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Front-door log of every customer RFQ.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={async () => {
              const params = new URLSearchParams();
              if (filters.q) params.set("q", filters.q);
              if (filters.status) params.set("status", filters.status);
              if (filters.customer_id) params.set("customer_id", filters.customer_id);
              if (filters.unit_id && user?.role === "admin") params.set("unit_id", filters.unit_id);
              const res = await api.get(`/rfqs/export.xlsx?${params.toString()}`, { responseType: "blob" });
              const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "AAMPL_RFQs.xlsx";
              link.click();
              URL.revokeObjectURL(link.href);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50"
            data-testid="rfq-export-btn"
          >
            <Download size={14} /> Export Excel
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search RFQ, part, model…" value={filters.q}
            onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
            className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md w-64" data-testid="rfq-search" />
        </div>
        <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">Status: All</option>
          {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          <option value="in_flight">In Flight (received/costing/quote_sent)</option>
        </select>
        <select value={filters.customer_id} onChange={e => setFilters(f => ({ ...f, customer_id: e.target.value }))} className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">Customer: All</option>
          {customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
        </select>
        {user?.role === "admin" && (
          <select value={filters.unit_id} onChange={e => setFilters(f => ({ ...f, unit_id: e.target.value }))} className="text-sm border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">Plant: All</option>
            {units.map(u => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
          </select>
        )}
        {/* Date-wise sort by Received date */}
        <select value={dateSort} onChange={e => setDateSort(e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5" data-testid="rfq-date-sort">
          <option value="desc">Date: Newest first</option>
          <option value="asc">Date: Oldest first</option>
        </select>
      </div>
      <div className="grid grid-cols-5 gap-2 mb-4">
        {[["Total", kpi.total],["Open", kpi.open],["Won", kpi.won],["Lost", kpi.lost],["Dropped", kpi.dropped]].map(([label, v]) => (
          <div key={label} className="bg-slate-50 rounded-md px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-xl font-semibold mt-0.5">{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-8"></th>
              <th className="text-left px-3 py-2 font-medium">RFQ #</th>
              <th className="text-left px-3 py-2 font-medium">Customer</th>
              <th className="text-left px-3 py-2 font-medium">Model</th>
              <th className="text-left px-3 py-2 font-medium">Parts</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Value (₹)</th>
              <th className="text-left px-3 py-2 font-medium cursor-pointer select-none hover:text-orange-600" onClick={() => setDateSort(s => (s === "asc" ? "desc" : "asc"))} title="Click to sort by Received date" data-testid="rfq-recv-sort">
                Recv {dateSort === "asc" ? "▲" : "▼"}
              </th>
              <th className="text-left px-3 py-2 font-medium">Linked</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rfqs.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-slate-400">No RFQs are currently logged. New RFQs appear here automatically when you create an NPD, Project, or Assembly.</td></tr>}
            {!loading && sortedRfqs.map(r => {
              const badge = STATUS_BADGE[r.status] || STATUS_BADGE.received;
              const isOpen = expanded.has(r.rfq_id);
              return (
                <Fragment key={r.rfq_id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(r.rfq_id)} data-testid={`rfq-row-${r.rfq_number}`}>
                    <td className="px-2"><ChevronRight size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-90 text-orange-500" : ""}`} /></td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/rfqs/${r.rfq_id}`} onClick={e => e.stopPropagation()} className="text-slate-900 hover:text-orange-600">{r.rfq_number}</Link>
                      {(() => {
                        const count = (r.mail_attachment_path ? 1 : 0) + ((r.misc_files || []).length);
                        return count > 0 ? (
                          <div className="flex items-center gap-1 text-slate-500 text-[10px] mt-0.5" title={`${count} attachment${count > 1 ? "s" : ""}`} data-testid={`rfq-attachments-${r.rfq_number}`}>
                            <Paperclip size={11} /> {count}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2">
                      <div>{r.model_name || <span className="text-slate-400">—</span>}</div>
                      {r.linked_entity_number && (
                        <Link
                          to={
                            r.linked_entity_kind === "project" ? `/projects/${r.linked_project_id}` :
                            r.linked_entity_kind === "assembly" ? `/assemblies/${r.linked_assembly_id}` :
                            r.linked_entity_kind === "npd" ? `/npd/${r.linked_npd_id}` : "#"
                          }
                          className="text-[11px] font-mono text-slate-500 hover:text-orange-600 hover:underline"
                          data-testid={`linked-number-${r.rfq_id}`}
                        >
                          {r.linked_entity_number}
                        </Link>
                      )}
                    </td>
                    <td className="px-3 py-2" data-testid={`parts-count-${r.rfq_id}`}>
                      {r.parts_count ?? (r.parts || []).length}
                    </td>
                    <td className="px-3 py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${badge.cls}`}>{badge.label}</span></td>
                    <td className="px-3 py-2">{shortValue(r.business_value_live)}</td>
                    <td className="px-3 py-2 text-slate-600">{r.received_date}</td>
                    <td className="px-3 py-2">
                      {r.linked_project_id ? <Link to={`/projects/${r.linked_project_id}`} className="text-sky-600 text-xs">Project</Link> :
                       r.linked_assembly_id ? <Link to={`/assemblies/${r.linked_assembly_id}`} className="text-sky-600 text-xs">Assembly</Link> :
                       r.linked_npd_id ? <Link to={`/npd/${r.linked_npd_id}`} className="text-sky-600 text-xs">NPD</Link> :
                       <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50">
                      <td colSpan={9} className="px-6 py-3">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Parts in this RFQ</div>
                        {(r.parts || []).length === 0 && r.linked_entity_kind && (r.parts_count || 0) > 0 ? (
                          <Link
                            to={
                              r.linked_entity_kind === "project" ? `/projects/${r.linked_project_id}` :
                              r.linked_entity_kind === "assembly" ? `/assemblies/${r.linked_assembly_id}` :
                              `/npd/${r.linked_npd_id}`
                            }
                            className="text-sm text-orange-600 hover:underline"
                            data-testid={`linked-parts-link-${r.rfq_id}`}
                          >
                            View {r.parts_count} part{r.parts_count === 1 ? "" : "s"} in linked {r.linked_entity_kind} →
                          </Link>
                        ) : (
                          <div className="overflow-x-auto">
                          <table className="w-full min-w-[600px] text-xs">
                            <thead className="text-slate-500">
                              <tr><th className="text-left py-1 font-medium">Part name</th><th className="text-left py-1 font-medium">Part #</th><th className="text-left py-1 font-medium">Monthly qty</th><th className="text-left py-1 font-medium">Target ₹/pc</th><th className="text-left py-1 font-medium">Drawing</th></tr>
                            </thead>
                            <tbody>
                              {(r.parts || []).map(p => (
                                <tr key={p.part_id} className="border-t border-dashed border-slate-200">
                                  <td className="py-1.5">{p.part_name}</td>
                                  <td className="py-1.5 font-mono">{p.part_number}</td>
                                  <td className="py-1.5">{Number(p.monthly_volume_pcs || 0).toLocaleString("en-IN")} <span className="text-slate-400 text-[10px]">≈ {Number((p.monthly_volume_pcs || 0) * 12).toLocaleString("en-IN")}/yr</span></td>
                                  <td className="py-1.5">{p.target_price_per_pc ? formatINR(p.target_price_per_pc) : "—"}</td>
                                  <td className="py-1.5">{p.drawing_original_filename ? <span className="inline-flex items-center gap-1 text-sky-600"><Paperclip size={12} />{p.drawing_original_filename}</span> : <span className="text-slate-400">—</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
